import { ComponentProps, useCallback, useMemo, useState } from 'react';

import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChangeOptions,
  useMultipleSelection,
  UseMultipleSelectionState,
  UseMultipleSelectionStateChangeOptions,
} from 'downshift';

import useFilteredItems from './useFilteredItems';
import { defaultLabelGetter } from '../helpers';
import { CreateNewMenuItem } from '../layout';
import { GenericItem, GetFilteredItems, UseCreatableItemProps } from '../types';

type UseCreatableItemStateReducer<TItem extends GenericItem> = (
  state: UseComboboxState<TItem>,
  actionAndChanges: UseComboboxStateChangeOptions<TItem>,
) => Partial<UseComboboxState<TItem>>;

type UseMultiCreatableItemStateReducer<TItem extends GenericItem> = (
  state: UseMultipleSelectionState<TItem>,
  actionAndChanges: UseMultipleSelectionStateChangeOptions<TItem>,
) => Partial<UseMultipleSelectionState<TItem>>;

type UseCreatableItemReturnValue<TItem extends GenericItem> = {
  items: TItem[];
  initialInputValue?: string;
  creatableItem?: TItem;
  getCreatableItemProps: () => ComponentProps<typeof CreateNewMenuItem>;
  stateReducer?: UseCreatableItemStateReducer<TItem>;
  multipleSelectionStateReducer?: UseMultiCreatableItemStateReducer<TItem>;
  getFilteredItems?: GetFilteredItems<TItem>;
};

export const defaultCreateItem = <TItem extends GenericItem>(
  label: string,
): TItem =>
  ({
    label,
    value: label,
  }) as unknown as TItem;

export default <TItem extends GenericItem>({
  items: itemsProp,
  initialInputValue,
  initialCreatedItems = [],
  createItem = defaultCreateItem,
  itemToString = defaultLabelGetter,
  getFilteredItems,
}: UseCreatableItemProps<TItem>): UseCreatableItemReturnValue<TItem> => {
  const [isMultiple, setIsMultiple] = useState(false);

  const [internalInputValue, setInternalInputValue] = useState<
    string | undefined
  >(initialInputValue);

  const [createdItems, setCreatedItems] =
    useState<TItem[]>(initialCreatedItems);

  const itemsWithCreated = useMemo(
    () => createdItems.concat(itemsProp),
    [createdItems, itemsProp],
  );

  const { filteredItems: exactMatchingItems } = useFilteredItems({
    items: itemsWithCreated,
    itemToString,
    getFilteredItems,
    inputValue: internalInputValue,
    filterBy: (item, inputValue) => item === inputValue,
  });

  const creatableItem = useMemo(
    () =>
      !internalInputValue || exactMatchingItems.length > 0
        ? undefined
        : createItem(internalInputValue),
    [createItem, exactMatchingItems.length, internalInputValue],
  );

  const allItems = useMemo(
    () =>
      creatableItem ? itemsWithCreated.concat(creatableItem) : itemsWithCreated,
    [creatableItem, itemsWithCreated],
  );

  const getCreatableItemProps = useCallback(
    (): ComponentProps<typeof CreateNewMenuItem> => ({
      value: internalInputValue ?? '',
    }),
    [internalInputValue],
  );

  const stateReducer = useCallback<UseCreatableItemStateReducer<TItem>>(
    (state, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInternalInputValue(changes.inputValue);
          return changes;
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.FunctionSelectItem:
          if (isMultiple) {
            setInternalInputValue(undefined);
            return { ...changes, inputValue: '', isOpen: true };
          }
          // remove all unselected created items
          setCreatedItems((prev) => {
            return prev.filter((item) => item === changes.selectedItem);
          });

          if (creatableItem && changes.selectedItem === creatableItem) {
            // move creatable item to created items
            setCreatedItems((prev) => [creatableItem, ...prev]);
            setInternalInputValue(undefined);
          }
          return changes;
        default:
          return changes;
      }
    },
    [creatableItem, isMultiple],
  );

  const multipleSelectionStateReducer = useCallback<
    UseMultiCreatableItemStateReducer<TItem>
  >(
    (state, { type, changes }) => {
      setIsMultiple(true);
      switch (type) {
        case useMultipleSelection.stateChangeTypes.FunctionAddSelectedItem:
          if (creatableItem && changes.selectedItems?.includes(creatableItem)) {
            // move creatable item to created items
            setCreatedItems([creatableItem, ...createdItems]);
          }

          break;
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          // remove unselected creatable items
          setCreatedItems((prev) =>
            prev.filter((item) => changes.selectedItems?.includes(item)),
          );

          break;
        default:
          break;
      }

      return changes;
    },
    [creatableItem, createdItems],
  );

  return {
    items: allItems,
    initialInputValue,
    creatableItem,
    getCreatableItemProps,
    getFilteredItems,
    stateReducer,
    multipleSelectionStateReducer,
  };
};
